import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Carregando from "./Carregando";
import { frase, lerPainel, type Painel, type Resposta } from "../lib/sentinela";
import { ListaDeCpfs, ListaDeEmpresas } from "./SentinelaListas";
import SentinelaDossie from "./SentinelaDossie";
import { AlertasDoSentinela, ConsumoDoSentinela } from "./SentinelaAlertasConsumo";
import { AvisoDaVirada } from "./SentinelaUi";

/**
 * Admin › Sentinela (000397, fase 3a): as telas do app de lá, SÓ LEITURA, sobre a cópia contínua. Até a virada
 * elas são da equipe (a aba `sentinela` da matriz) e mostram o acervo inteiro; na virada o mesmo conjunto vai
 * para o aluno, em `/ferramenta/sentinela`, com o recorte dele — por isso o endereço de cada tela é uma QUERY
 * sobre uma base (`?secao=`, `?empresa=`, `?cpf=`, `&aba=`), e não uma rota do painel: o mesmo componente monta
 * nos dois lugares trocando só a base.
 */

const BASE = "/admin/sentinela";
type Secao = "painel" | "cpfs" | "alertas" | "consumo";

export default function AdminSentinela() {
  const [busca, setBusca] = useSearchParams();
  const [painel, setPainel] = useState<Painel | Resposta | null>(null);

  useEffect(() => {
    let vivo = true;
    void lerPainel().then((x) => vivo && setPainel(x));
    return () => {
      vivo = false;
    };
  }, []);

  const empresa = busca.get("empresa");
  const cpf = busca.get("cpf");
  const pedida = busca.get("secao");
  const secao: Secao = pedida === "cpfs" || pedida === "alertas" || pedida === "consumo" ? pedida : "painel";
  const ativa: Secao = empresa ? "painel" : cpf ? "cpfs" : secao;

  if (!painel) return <Carregando texto="Lendo o Sentinela…" />;
  if (!painel.ok) return <p className="surface rounded-cf p-5 text-sm text-tinta" data-sentinela-recusado>{frase(painel)}</p>;

  const naoLidos = painel.alertas_nao_lidos;
  const SECOES: { id: Secao; rotulo: string }[] = [
    { id: "painel", rotulo: "Terceiros" },
    { id: "cpfs", rotulo: "CPFs monitorados" },
    { id: "alertas", rotulo: naoLidos > 0 ? `Alertas (${naoLidos} não ${naoLidos === 1 ? "lido" : "lidos"})` : "Alertas" },
    { id: "consumo", rotulo: "Consumo de API" },
  ];
  const ir = (s: Secao) => {
    const b = new URLSearchParams();
    if (s !== "painel") b.set("secao", s);
    setBusca(b);
  };

  return (
    <div className="flex flex-col gap-5" data-sentinela data-escopo={painel.escopo}>
      <div className="max-w-3xl">
        <h2 className="font-display text-2xl font-bold text-titulo">Sentinela</h2>
        <p className="mt-1 text-sm leading-relaxed text-cinza">
          O monitoramento de riscos de terceiros da Pós Auditoria: os terceiros e os CPFs que os alunos monitoram, o dossiê de cada
          um, os alertas e o consumo das fontes.
        </p>
      </div>
      <AvisoDaVirada />
      <nav className="flex flex-wrap gap-2" aria-label="Seções do Sentinela">
        {SECOES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => ir(s.id)}
            aria-current={ativa === s.id ? "page" : undefined}
            className={`min-h-10 rounded-full px-4 text-sm font-semibold transition ${
              ativa === s.id ? "bg-acento text-acento-tinta" : "border border-borda text-tinta hover:border-acento"
            }`}
            data-secao-sentinela={s.id}
          >
            {s.rotulo}
          </button>
        ))}
      </nav>

      {empresa ? (
        <SentinelaDossie key={`empresa:${empresa}`} tipo="empresa" id={empresa} base={BASE} />
      ) : cpf ? (
        <SentinelaDossie key={`cpf:${cpf}`} tipo="cpf" id={cpf} base={BASE} />
      ) : secao === "cpfs" ? (
        <ListaDeCpfs painel={painel} base={BASE} />
      ) : secao === "alertas" ? (
        <AlertasDoSentinela base={BASE} />
      ) : secao === "consumo" ? (
        <ConsumoDoSentinela base={BASE} />
      ) : (
        <ListaDeEmpresas painel={painel} base={BASE} />
      )}
    </div>
  );
}
